import * as React from 'react';
import { Autocomplete, type AutocompleteProps, CircularProgress, Box, TextFieldProps } from '@mui/material';
import _ from 'lodash';
import TextFieldWithTooltip from '../../common/TextFieldWithTooltip/TextFieldWithTooltip';
import PreferencePill from '../PreferencePill';
import { Preference } from '../../../types/Preference';

type DerivedProps<
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = undefined,
  ChipComponent extends React.ElementType = 'div',
> = Omit<AutocompleteProps<Preference, Multiple, DisableClearable, false, ChipComponent>, 'renderInput'> &
  Pick<TextFieldProps, 'label' | 'error' | 'helperText' | 'required'>;

export type PreferencePickerProps<
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = undefined,
  ChipComponent extends React.ElementType = 'div',
> = DerivedProps<Multiple, DisableClearable, ChipComponent> & {
  loading?: boolean;
  tooltip?: string;
  /** Value-only change handler (Preference | null) OR Preference[] when multiple=true */
  onValueChange?: (value: AutocompleteProps<Preference, Multiple, DisableClearable, false, ChipComponent>['value']) => void;
};

const defaultIsOptionEqualToValue = (option: Preference, value: Preference) => option.uuid === value.uuid;
const defaultGetOptionLabel = (option: Preference) => option.value;

export default function PreferencePicker<
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = undefined,
  ChipComponent extends React.ElementType = 'div',
>({
  label = 'Preferences',
  isOptionEqualToValue = defaultIsOptionEqualToValue,
  getOptionLabel = defaultGetOptionLabel,
  loading,
  options,
  value,
  onValueChange,
  multiple,
  error,
  helperText,
  required,
  tooltip,
  ...otherProps
}: PreferencePickerProps<Multiple, DisableClearable, ChipComponent>) {
  type ResolverAutocompleteProps = AutocompleteProps<Preference, Multiple, DisableClearable, false, ChipComponent>;
  type ResolvedPreferencePickerProps = PreferencePickerProps<Multiple, DisableClearable, ChipComponent>;
  type ValueType = ResolvedPreferencePickerProps['value'];

  const onChange: ResolvedPreferencePickerProps['onChange'] = (_evt, v: ValueType) => {
    onValueChange?.(v);
  };

  const selectedList = React.useMemo<Preference[]>(() => {
    if (multiple) return value as Preference[];
    return value ? [value as Preference] : [];
  }, [value, multiple]);

  const handleDeleteChip = (uuid: string) => {
    if (multiple) {
      const next = _.reject(selectedList, { uuid });
      onValueChange?.(next as ValueType);
    } else {
      onValueChange?.(null as ValueType);
    }
  };

  const renderOption: ResolvedPreferencePickerProps['renderOption'] = (props, option) => {
    return (
      <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', py: 0.5 }}>
        <PreferencePill {...option} />
      </Box>
    );
  };

  const renderInput: ResolverAutocompleteProps['renderInput'] = (params) => {
    const chips = _.map(selectedList, (pref) => <PreferencePill {...pref} key={pref.uuid} onDelete={() => handleDeleteChip(pref.uuid)} />);
    const { ref: inputRef, ...restInputProps } = params.inputProps;
    const fieldProps: TextFieldProps = {
      ...params,
      label,
      size: 'small',
      error,
      helperText,
      required,
      slotProps: {
        input: {
          ...params.InputProps,
          inputRef,
          startAdornment: multiple ? <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>{chips}</Box> : params.InputProps.startAdornment,
          endAdornment: (
            <>
              {loading ? <CircularProgress size={18} /> : null}
              {params.InputProps.endAdornment}
            </>
          ),
        },
        htmlInput: {
          ...restInputProps,
          maxLength: 100,
          'aria-label': label,
        },
      },
    };
    return <TextFieldWithTooltip {...fieldProps} tooltip={tooltip} />;
  };

  return (
    <Autocomplete<Preference, Multiple, DisableClearable, false, ChipComponent>
      multiple={multiple}
      options={options}
      value={value}
      onChange={onChange}
      isOptionEqualToValue={isOptionEqualToValue}
      getOptionLabel={getOptionLabel}
      renderOption={renderOption}
      renderInput={renderInput}
      {...otherProps}
    />
  );
}
